import { ActiveMediaDevicePayload } from 'features/user-media/types';
import { logSwitchCaptureFailure } from 'features/user-media/utils/logSwitchCaptureFailure';
import { reportMediaCaptureFailure } from 'features/user-media/utils/reportMediaCaptureFailure';
import { stopStreamTracks } from 'features/user-media/utils';
import { getDefaultVideoConstraints } from 'features/user-media/utils/getDefaultVideoConstraints';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { VideoBroadcastReconciler } from 'utils/webrtc/publishing/VideoBroadcastReconciler';
import { FeedMediaStates, LocalFeedStream } from 'utils/webrtc/types';
import { ControlledPublishingHandle } from 'utils/webrtc/publishing/ControlledPublishingHandle';

// Host members are read at call time, never captured: `ScreensharingFeed`'s field initializer
// REPLACES `mediaStates` after super() runs, `handle` is reassigned per attach, and tests
// monkey-patch host members on the feed instance after construction.
export interface DeviceSwitcherHost {
  handle?: ControlledPublishingHandle;
  mediaStates: FeedMediaStates;
  mediaController: {
    requestExternalStream: (constraints?: MediaStreamConstraints) => Promise<MediaStream>;
    streams: Record<string, LocalFeedStream>;
    removeStream: (id: string) => void;
  };
  getPeerConnection: () => RTCPeerConnection | undefined;
  getVideoMid: () => string | undefined;
  hasVideoTransceiver: () => boolean;
  updateRedux: () => void;
  getAudioMid: () => string | undefined;
  changeVideoDeviceWithVB: (device: ActiveMediaDevicePayload) => Promise<void>;
}

export class DeviceSwitcher {
  constructor(
    private host: DeviceSwitcherHost,
    private reconciler: VideoBroadcastReconciler
  ) {}

  changeAudioDevice = async (device: ActiveMediaDevicePayload) => {
    const mid = this.host.getAudioMid();
    if (!mid) {
      return;
    }

    let track: MediaStreamTrack | undefined;
    try {
      const stream = await this.host.mediaController.requestExternalStream({
        audio: { deviceId: { exact: device.id } },
      });
      track = stream?.getAudioTracks()[0];
    } catch (error) {
      logSwitchCaptureFailure('Audio device switch', error);
      return;
    }

    if (!track) {
      return;
    }

    // gUM hands back an enabled track, and nothing else applies the muted intent on this path
    // until the SFU media-state round trip.
    track.enabled = this.host.mediaStates.audio.broadcasting;

    await this.replaceAudioTrack(track, mid);
  };

  changeVideoDeviceWithVB = async (device: ActiveMediaDevicePayload) => {
    if (!this.host.hasVideoTransceiver() || !this.host.mediaStates.video.enabled) {
      return;
    }

    let sourceStream: MediaStream;
    try {
      sourceStream = await this.host.mediaController.requestExternalStream({
        video: { ...getDefaultVideoConstraints(), deviceId: { exact: device.id } },
      });
    } catch (error) {
      logSwitchCaptureFailure('Video device switch (virtual background)', error);
      return;
    }

    // VB consumes (and stops) the source video track internally; on failure stop whatever remains.
    const processed = await VirtualBackgroundManager.updateStream({ stream: sourceStream });
    if (!processed) {
      stopStreamTracks(sourceStream);
      return;
    }

    const track = processed.getVideoTracks()[0];
    if (track) {
      await this.reconciler.replaceVideoTrack(track, this.host.getVideoMid());
    }
  };

  changeVideoDevice = async (device: ActiveMediaDevicePayload) => {
    if (VirtualBackgroundManager.activated) {
      await this.host.changeVideoDeviceWithVB(device);
      return;
    }

    if (!this.host.hasVideoTransceiver() || !this.host.mediaStates.video.enabled) {
      return;
    }

    const mid = this.host.getVideoMid();

    // Release the current camera before capturing the new one: Android permits only one open
    // camera per process, so a capture-first swap rejects while the old track holds the sensor.
    const outgoingTrack =
      this.host
        .getPeerConnection()
        ?.getTransceivers()
        .find((transceiver) => transceiver.sender?.track?.kind === 'video')?.sender?.track ?? null;
    const previousDeviceId = outgoingTrack?.getSettings?.().deviceId;
    if (outgoingTrack) {
      await this.reconciler.replaceVideoTrack(null, mid);
      outgoingTrack.stop();
      // Also release the self-view's independent camera clone: the sensor frees only when every
      // track from the source stops, and its teardown is otherwise deferred behind an async call.
      Object.values(this.host.mediaController.streams)
        .filter((stream) => stream.kind === 'video')
        .forEach((stream) => this.host.mediaController.removeStream(stream.id));
    }

    let track: MediaStreamTrack | undefined;
    let captureError: unknown;
    try {
      const stream = await this.host.mediaController.requestExternalStream({
        video: { ...getDefaultVideoConstraints(), deviceId: { exact: device.id } },
      });
      track = stream?.getVideoTracks()[0];
    } catch (error) {
      captureError = error;
      logSwitchCaptureFailure('Video device switch', error);
    }

    // The old camera is already released; re-acquire it so a failed switch degrades to
    // "camera unchanged" instead of dead video.
    if (!track && previousDeviceId) {
      try {
        const stream = await this.host.mediaController.requestExternalStream({
          video: { ...getDefaultVideoConstraints(), deviceId: { exact: previousDeviceId } },
        });
        track = stream?.getVideoTracks()[0];
      } catch (error) {
        logSwitchCaptureFailure('Video device switch rollback', error);
      }
    }

    // New camera and previous camera both failed → video ends off: mirror the mute signaling
    // and surface a genuine capture failure.
    if (!track) {
      this.reconciler.signalVideoBroadcast(false);
      reportMediaCaptureFailure('videoinput', captureError);
      this.host.mediaStates.video.enabled = false;
      this.host.mediaStates.video.broadcasting = false;
      this.host.mediaStates.video.captured = false;
      this.host.updateRedux();
      return;
    }

    await this.reconciler.replaceVideoTrack(track, mid);
  };

  // The caller owns track.enabled; janus would otherwise carry it over from the outgoing track,
  // whose own `enabled` is stale once that track has been stopped.
  private replaceAudioTrack = (track: MediaStreamTrack, mid: string | undefined): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      if (!this.host.handle || !mid) {
        resolve();
        return;
      }
      this.host.handle.videoroom.replaceTracks({
        tracks: [{ type: 'audio', mid, capture: track, preserveMuteState: false }],
        success: () => resolve(),
        error: (e: unknown) => reject(e),
      });
    });
}
